import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

const rand = (a, b) => a + Math.random() * (b - a);
const STEPS = 10;

const trajectory = (vx, vy, g, T) => {
  const xs = [];
  const ys = [];
  for (let i = 0; i <= STEPS; i++) {
    const t = (i / STEPS) * T;
    xs.push(vx * t);
    ys.push(vy * t + 0.5 * g * t * t);
  }
  return { xs, ys };
};

const dropStyle = (p) => ({
  left: p.ox - p.size / 2,
  top: p.oy - (p.size * p.stretch) / 2,
  width: p.size,
  height: p.size * p.stretch,
  borderRadius: "50% 50% 50% 50% / 62% 62% 38% 38%",
  background: `radial-gradient(circle at 35% 30%, rgba(255,255,255,0.95) 0%, ${p.color} 35%, ${p.colorDeep} 100%)`,
  boxShadow: `0 0 ${p.size * 0.8}px ${p.color}66`,
});

const makeDrops = ({ n, ox, oy, minSpeed, maxSpeed, angleFrom, angleTo, g, T, minSize, maxSize, delay }) =>
  Array.from({ length: n }, (_, i) => {
    const a = rand(angleFrom, angleTo) * (Math.PI / 180);
    const s = rand(minSpeed, maxSpeed);
    const vx = Math.cos(a) * s;
    const vy = Math.sin(a) * s;
    const dur = T * rand(0.8, 1.15);
    const { xs, ys } = trajectory(vx, vy, g, dur);
    const rots = xs.map((x, k) => (k === 0 ? 0 : (Math.atan2(ys[k] - ys[k - 1], xs[k] - xs[k - 1]) * 180) / Math.PI + 90));
    rots[0] = rots[1];
    const size = rand(minSize, maxSize);
    const cyan = i % 4 === 0;
    return {
      id: i,
      ox,
      oy,
      xs,
      ys,
      rots,
      size,
      stretch: rand(1.25, 1.9),
      dur,
      d: delay + rand(0, 0.18),
      color: cyan ? "#9be0ff" : "#5fb6ff",
      colorDeep: cyan ? "#1B72D9" : "#0e4fa8",
    };
  });

const Drop = ({ p }) => (
  <motion.span
    className="absolute"
    style={dropStyle(p)}
    initial={{ x: 0, y: 0, opacity: 0, scale: 0.4, rotate: p.rots[0] }}
    animate={{
      x: p.xs,
      y: p.ys,
      rotate: p.rots,
      opacity: [0, 1, 1, 1, 1, 1, 1, 0.95, 0.8, 0.5, 0],
      scale: [0.4, 1, 1, 1, 0.98, 0.95, 0.92, 0.88, 0.8, 0.7, 0.5],
    }}
    transition={{ duration: p.dur, delay: p.d, ease: "linear" }}
  />
);

export const WaterSplash = ({ originRef, delay: initialDelay = 0.5, life: fullLife = 3000, trigger = 0, mini = false }) => {
  const life = mini ? 1300 : fullLife;
  const [alive, setAlive] = useState(true);
  const [origin, setOrigin] = useState(null);
  const delay = trigger ? 0.02 : initialDelay;

  useEffect(() => {
    const el = originRef?.current;
    const r = el ? el.getBoundingClientRect() : null;
    setOrigin(r ? { x: r.left + r.width / 2, y: r.top + r.height / 2, top: r.top, w: r.width } : { x: 120, y: 60, top: 10, w: 160 });
    setAlive(true);
    const t = setTimeout(() => setAlive(false), delay * 1000 + life);
    return () => clearTimeout(t);
  }, [originRef, delay, life, trigger]);

  const parts = useMemo(() => {
    if (!origin) return null;
    const reach = Math.hypot(window.innerWidth, window.innerHeight);
    if (mini) {
      const crown = makeDrops({ n: 16, ox: origin.x, oy: origin.top + 8, minSpeed: 160, maxSpeed: 400, angleFrom: -140, angleTo: -40, g: 1400, T: 0.85, minSize: 4, maxSize: 10, delay });
      return { crown, spray: [], mist: Array.from({ length: 4 }, (_, i) => ({ id: i, x: rand(-60, 60), y: rand(-90, -20), s: rand(30, 60), d: delay + rand(0, 0.1) })) };
    }
    const crown = makeDrops({ n: 44, ox: origin.x, oy: origin.top + 6, minSpeed: 240, maxSpeed: 600, angleFrom: -150, angleTo: -30, g: 1300, T: 1.25, minSize: 6, maxSize: 16, delay });
    const spray = makeDrops({ n: 70, ox: origin.x, oy: origin.y, minSpeed: reach * 0.35, maxSpeed: reach * 0.85, angleFrom: 0, angleTo: 360, g: 600, T: 2, minSize: 7, maxSize: 20, delay: delay + 0.05 });
    const mist = Array.from({ length: 14 }, (_, i) => ({ id: i, x: rand(-160, 160), y: rand(-220, -30), s: rand(50, 130), d: delay + rand(0, 0.2) }));
    return { crown, spray, mist };
  }, [origin, delay, mini]);

  if (!origin || !parts) return null;

  return createPortal(
    <AnimatePresence>
      {alive && (
        <motion.div key={trigger} className="fixed inset-0 z-[95] pointer-events-none overflow-hidden" exit={{ opacity: 0 }} transition={{ duration: 0.5 }} aria-hidden data-testid={mini ? "logo-water-spurt" : "logo-water-splash"}>
          {!mini && (<>
          <motion.span
            className="absolute rounded-full"
            style={{ left: origin.x, top: origin.y, translateX: "-50%", translateY: "-50%", background: "radial-gradient(circle, rgba(155,224,255,0.5) 0%, rgba(27,114,217,0.28) 35%, rgba(27,114,217,0) 70%)" }}
            initial={{ width: 40, height: 40, opacity: 1 }}
            animate={{ width: 3200, height: 3200, opacity: 0 }}
            transition={{ duration: 1.7, delay, ease: "easeOut" }}
          />
          {[0, 0.12, 0.26].map((off, i) => (
            <motion.span
              key={`ring-${off}`}
              className="absolute rounded-full"
              style={{ left: origin.x, top: origin.y, translateX: "-50%", translateY: "-50%", border: `${5 - i}px solid rgba(155,224,255,${0.85 - i * 0.2})`, boxShadow: "0 0 30px rgba(107,188,253,0.5)" }}
              initial={{ width: 30, height: 30, opacity: 1 }}
              animate={{ width: 2800 - i * 500, height: 2800 - i * 500, opacity: 0 }}
              transition={{ duration: 1.5, delay: delay + off, ease: [0.1, 0.8, 0.3, 1] }}
            />
          ))}
          </>)}

          <motion.span
            className="absolute"
            style={{ left: origin.x, top: origin.top + 10, translateX: "-50%", translateY: "-100%", width: origin.w * 1.1, height: origin.w * 0.5, borderRadius: "50% 50% 50% 50% / 100% 100% 0 0", background: "radial-gradient(ellipse at 50% 100%, rgba(255,255,255,0.9) 0%, rgba(155,224,255,0.7) 30%, rgba(107,188,253,0) 75%)", filter: "blur(2px)" }}
            initial={{ scaleY: 0, scaleX: 0.5, opacity: 0 }}
            animate={{ scaleY: [0, 1.6, 1.1, 0.6], scaleX: [0.5, 1, 1.25, 1.4], opacity: [0, 1, 0.6, 0] }}
            transition={{ duration: 0.9, delay, ease: "easeOut", times: [0, 0.35, 0.65, 1] }}
          />
          {parts.mist.map((m) => (
            <motion.span
              key={m.id}
              className="absolute rounded-full"
              style={{ left: origin.x, top: origin.top, width: m.s, height: m.s, background: "radial-gradient(circle, rgba(200,236,255,0.55) 0%, rgba(107,188,253,0) 70%)", filter: "blur(6px)" }}
              initial={{ x: 0, y: 0, opacity: 0, scale: 0.3 }}
              animate={{ x: m.x, y: m.y, opacity: [0, 0.9, 0], scale: [0.3, 1.4, 1.8] }}
              transition={{ duration: 1.4, delay: m.d, ease: "easeOut" }}
            />
          ))}
          {parts.crown.map((p) => <Drop key={`c${p.id}`} p={p} />)}
          {parts.spray.map((p) => <Drop key={`s${p.id}`} p={p} />)}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
